import React, { Fragment } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Button } from 'reactstrap';
import EyeIcon from 'mdi-react/EyeIcon';
import SettingsIcon from 'mdi-react/SettingsIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import BinIcon from 'mdi-react/BinIcon';
import { useSubcategoryUIContext } from '../SubcategoryUIProvider';
import SubcategoryTableColumns, { SubCategoryColumns } from './SubcategoryTableColumns';
import { SubcategoryTableSearchBar } from './SubcategoryTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getSubCategoriesList, setSubCategoryFilters } from '../../redux/actions';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';

const SubcategoryTable = () => {
  const subcategoryUIContext = useSubcategoryUIContext();
  const dispatch = useDispatch();
  const {
    data: subCategories,
    error,
    filters,
    loading,
    totalRecords,
  } = useSelector((state) => state.subCategories, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  React.useEffect(() => {
    dispatch(getSubCategoriesList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setSubCategoryFilters({ sortBy: property, isSortOrderDescending: !isSortOrderDescending }),
    );
  };

  const handleChangePage = (event, currentPage) => {
    dispatch(setSubCategoryFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setSubCategoryFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - subCategories.length : 0;

  const renderActionButtons = ({ id: subCategoryId }) => (
    <div className="actions">
      <Button
        className="icon mb-2"
        color="secondary"
        outline
        onClick={() => subcategoryUIContext.openManageSkillsDialog(subCategoryId)}
      >
        <p>
          <SettingsIcon />
          Manage Skills
        </p>
      </Button>
      <Button
        className="icon mb-2"
        color="success"
        outline
        onClick={() => subcategoryUIContext.openViewSubCategoryDialog(subCategoryId)}
      >
        <p>
          <EyeIcon /> View
        </p>
      </Button>
      <Button
        className="icon mb-2"
        color="primary"
        outline
        onClick={() => subcategoryUIContext.openEditSubCategoryDialog(subCategoryId)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
      <Button
        className="icon mb-2"
        color="danger"
        outline
        onClick={() => subcategoryUIContext.openDeleteSubCategoryDialog(subCategoryId)}
      >
        <p>
          <BinIcon /> Delete
        </p>
      </Button>
    </div>
  );

  return (
    <Fragment>
      <div className="material-table__wrap">
        <SubcategoryTableSearchBar />
        <Table className="material-table">
          <SubcategoryTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={SubCategoryColumns.length}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading subCategories...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!subCategories.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={SubCategoryColumns.length} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No sub-categories'
                    : 'No sub-categories with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {subCategories?.map((subCategory) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={subCategory.id}>
                  <TableCell
                    className="material-table__cell"
                    component="th"
                    align="center"
                    scope="row"
                  >
                    {subCategory?.name}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {subCategory?.description?.length > 65
                      ? subCategory?.description.substr(0, 65) + '...'
                      : subCategory?.description}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell pr-5">
                    {subCategory?.domains}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {subCategory?.skills}
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: '295px' }}
                  >
                    {renderActionButtons(subCategory)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={SubCategoryColumns.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default SubcategoryTable;
